<template>
  <Modal :model-value="show" @update:model-value="$emit('update:show', $event)">
    <template #title>
      <h2 class="text-lg font-semibold">{{ t('uploadInfringements') }}</h2>
    </template>

    <div class="space-y-4">
      <UploadDropzone
        v-model:files="files"
        :empty-hint="t('dropzoneHint')"
        :add-more-hint="t('dropzoneAddMore')"
        :remove-label="t('remove')"
        accept="image/jpeg,application/pdf,.jpg,.jpeg,.pdf"
      />

      <div v-if="files.length > 0" class="text-sm text-obsidian-600">
        {{ t('selectedFiles', { count: files.length }) }}
      </div>

      <div class="flex justify-end gap-2">
        <CVButton variant="outline" @click="$emit('update:show', false)">
          {{ t('cancel') }}
        </CVButton>
        <CVButton
          variant="default"
          :disabled="files.length === 0 || isUploading"
          :is-loading="isUploading"
          @click="handleUpload"
        >
          {{ t('upload') }}
        </CVButton>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/Modal.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import UploadDropzone, { type PreviewFile } from '@/components/UploadDropzone.vue';
import { useUploadInfringement } from '@/queries/use-upload';
import { useCreateInfringement } from '@/queries/use-infringements';
import { Alert } from '@/services/alert.service';

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
}>();

const { t } = useI18n();

const files = ref<PreviewFile[]>([]);
const fileObjects = ref<Map<string, File>>(new Map());
const { mutateAsync: uploadInfringement, isPending: isUploading } = useUploadInfringement();
const { mutateAsync: createInfringement } = useCreateInfringement();

const isSupportedInfringementFile = (file: File): boolean => {
  const fileName = file.name.toLowerCase();
  return (
    file.type === 'image/jpeg' ||
    file.type === 'application/pdf' ||
    fileName.endsWith('.jpg') ||
    fileName.endsWith('.jpeg') ||
    fileName.endsWith('.pdf')
  );
};

// Reset files when modal closes
watch(
  () => props.show,
  (isOpen) => {
    if (!isOpen) {
      files.value = [];
      fileObjects.value.clear();
    }
  },
);

// Store File objects when files are added
watch(
  () => files.value,
  async (newFiles) => {
    for (const previewFile of newFiles) {
      if (!fileObjects.value.has(previewFile.url)) {
        try {
          const response = await fetch(previewFile.url);
          const blob = await response.blob();
          const fileName = previewFile.name.toLowerCase();
          const mimeType = fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
          const file = new File([blob], previewFile.name, { type: mimeType });
          fileObjects.value.set(previewFile.url, file);
        } catch (error) {
          await Alert.fire({
            text: t('failedToConvertPreviewFile', { name: previewFile.name }),
            icon: 'error',
          });
          newFiles.splice(newFiles.indexOf(previewFile), 1);
        }
      }
    }
    // Remove files that are no longer in the list
    const currentUrls = new Set(newFiles.map((f) => f.url));
    for (const url of fileObjects.value.keys()) {
      if (!currentUrls.has(url)) {
        fileObjects.value.delete(url);
      }
    }
  },
  { deep: true },
);

const handleUpload = async () => {
  if (files.value.length === 0) return;

  try {
    // Get File objects from stored map
    const filesToUpload = files.value
      .map((previewFile) => fileObjects.value.get(previewFile.url))
      .filter((file): file is File => {
        if (!file) return false;
        return isSupportedInfringementFile(file);
      });

    if (filesToUpload.length === 0) {
      await Alert.fire({
        text: t('noSupportedFiles'),
        icon: 'error',
      });
      return;
    }

    // Upload each file and create infringement
    const uploadPromises = filesToUpload.map(async (file) => {
      const { fileKey } = await uploadInfringement(file);
      await createInfringement({
        imageFileKey: fileKey,
      });
    });

    await Promise.all(uploadPromises);

    emit('update:show', false);
  } catch (error) {
    console.error('Failed to upload infringements:', error);
    await Alert.fire({
      text: t('uploadFailed'),
      icon: 'error',
    });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "uploadInfringements": "Upload Infringements",
    "dropzoneHint": "Drop JPG or PDF files here or click to select",
    "dropzoneAddMore": "Add more",
    "remove": "Remove",
    "selectedFiles": "{count} file(s) selected",
    "cancel": "Cancel",
    "upload": "Upload",
    "noSupportedFiles": "Please select at least one JPG or PDF file",
    "infringementsCreated": "{count} infringement(s) created successfully",
    "uploadFailed": "Failed to upload infringements",
    "failedToConvertPreviewFile": "Failed to convert file {name} for upload"
  },
  "de": {
    "uploadInfringements": "Ordnungswidrigkeiten hochladen",
    "dropzoneHint": "JPG- oder PDF-Dateien hier ablegen oder klicken zum Auswählen",
    "dropzoneAddMore": "Weitere hinzufügen",
    "remove": "Entfernen",
    "selectedFiles": "{count} Datei(en) ausgewählt",
    "cancel": "Abbrechen",
    "upload": "Hochladen",
    "noSupportedFiles": "Bitte wählen Sie mindestens eine JPG- oder PDF-Datei aus",
    "infringementsCreated": "{count} Ordnungswidrigkeit(en) erfolgreich erstellt",
    "uploadFailed": "Fehler beim Hochladen der Ordnungswidrigkeiten",
    "failedToConvertPreviewFile": "Fehler beim Konvertieren der Datei {name} zum Hochladen"
  }
}
</i18n>
